/**
 * laylo-partners.js
 * Web Component: <laylo-partners>
 *
 * Sliding logo carousel. Shows a window of N logos at a time.
 * Prev/next arrows shift the window by one position per click.
 *
 * How it works:
 *   - The track is a flex row of all logo items at full width.
 *   - Each item is (100% / visible_count) wide.
 *   - Advancing by one shifts the track by -(1 / visible_count * 100%).
 *   - CSS transition handles the slide — no JS animation loop.
 *
 * State:
 *   _index   — current offset (0 = first logo at left edge)
 *   _max     — maximum offset = total - visible_count
 *
 * Progressive enhancement:
 *   Without JS: track renders as a wrapping flex row at natural widths.
 *   All logos are visible. Fully usable without the carousel behaviour.
 *
 * Accessibility:
 *   - aria-live="polite" on viewport announces position changes.
 *   - Buttons use aria-disabled (not HTML disabled) so they remain
 *     focusable for screen readers even when at boundary positions.
 *   - prefers-reduced-motion: transition removed, instant advance.
 *   - Autoplay pauses on hover and focus within the component.
 *
 * Performance:
 *   - Single delegated click listener on the Web Component root.
 *   - translateX only — compositor thread, no layout recalculation.
 *   - Autoplay uses setInterval, cleared on disconnect and on
 *     mouseover/focusin to respect user intent.
 */

class LayloPartners extends HTMLElement {
  connectedCallback() {
    this._track     = this.querySelector('.laylo-partners__track');
    this._viewport  = this.querySelector('.laylo-partners__viewport');
    this._announcer = this.querySelector('.laylo-partners__announcer');
    this._btnPrev  = this.querySelector('.laylo-partners__btn--prev');
    this._btnNext  = this.querySelector('.laylo-partners__btn--next');
    this._items    = this.querySelectorAll('.laylo-partners__item');

    this._visible = parseInt(this.dataset.visible || '5', 10);
    this._total   = parseInt(this.dataset.total   || '0', 10);
    this._index   = 0;
    this._max     = Math.max(0, this._total - this._visible);

    this._reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!this._track || this._total === 0) return;

    /*
      Static row mode — when total logos <= visible_count there is
      nothing to slide. All logos fit in the viewport simultaneously.
      Hide both buttons and return early. Item width is equal-width cells
      (100% / total) set inline here — overrides CSS's flex: 0 0 50% on
      mobile so all logos share one evenly-spaced row there too, and
      matches --lc-effective-visible's value in this branch (CSS already
      applies the same fraction before JS runs; this is just explicit).
    */
    if (this._total <= this._visible) {
      this._items.forEach(item => {
        /** @type {HTMLElement} */ (item).style.flex = `0 0 calc(100% / ${this._total})`;
      });
      if (this._btnPrev) this._btnPrev.hidden = true;
      if (this._btnNext) this._btnNext.hidden = true;
      return;
    }

    // Carousel mode — apply item widths so each fills exactly 1/visible_count
    this._items.forEach(item => {
      /** @type {HTMLElement} */ (item).style.flex = `0 0 calc(100% / ${this._visible})`;
    });

    // Remove transition temporarily for initial render
    this._track.style.transition = 'none';
    this._render();

    // Single delegated click listener
    this.addEventListener('click', this._onClick.bind(this));

    // Autoplay
    const autoplayMs = parseInt(this.dataset.autoplay || '0', 10);
    if (autoplayMs > 0) {
      this._startAutoplay(autoplayMs);
      // Pause on hover / focus
      this.addEventListener('mouseenter', () => this._stopAutoplay());
      this.addEventListener('focusin',    () => this._stopAutoplay());
      this.addEventListener('mouseleave', () => this._startAutoplay(autoplayMs));
      this.addEventListener('focusout',   () => this._startAutoplay(autoplayMs));
    }
  }

  disconnectedCallback() {
    this._stopAutoplay();
  }

  _onClick(e) {
    const btn = e.target.closest('.laylo-partners__btn');
    if (!btn) return;

    // Re-enable transition on first user interaction
    if (!this._reduced) {
      this._track.style.transition = 'transform 400ms cubic-bezier(0.22, 1, 0.36, 1)';
    }

    if (btn.classList.contains('laylo-partners__btn--prev')) {
      this._index = Math.max(0, this._index - 1);
    } else if (btn.classList.contains('laylo-partners__btn--next')) {
      this._index = Math.min(this._max, this._index + 1);
    }

    this._render();
  }

  _render() {
    // Batch reads
    const index   = this._index;
    const max     = this._max;
    const visible = this._visible;

    // Translate track — each step is one item width (1/visible of track)
    const pct = -(index * (100 / visible));
    this._track.style.transform = `translateX(${pct}%)`;

    // Update button states
    if (this._btnPrev) {
      const atStart = index === 0;
      this._btnPrev.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      this._btnPrev.classList.toggle('is-disabled', atStart);
    }

    if (this._btnNext) {
      const atEnd = index >= max;
      this._btnNext.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
      this._btnNext.classList.toggle('is-disabled', atEnd);
    }

    // Announce position to screen readers. aria-live="polite" is on
    // .laylo-partners__viewport; live regions announce content/text
    // mutations, not attribute changes on their own host element, so this
    // updates a text node inside it rather than the viewport's aria-label.
    if (this._announcer) {
      this._announcer.textContent =
        `Logos ${index + 1} to ${Math.min(index + visible, this._total)} of ${this._total}`;
    }
  }

  _advance() {
    if (!this._reduced) {
      this._track.style.transition = 'transform 400ms cubic-bezier(0.22, 1, 0.36, 1)';
    }
    this._index = this._index >= this._max ? 0 : this._index + 1;
    this._render();
  }

  _startAutoplay(ms) {
    this._stopAutoplay();
    this._timer = setInterval(() => this._advance(), ms);
  }

  _stopAutoplay() {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }
}

customElements.define('laylo-partners', LayloPartners);
